import {
  NodeKey,
  TextNode,
  Spread,
  SerializedTextNode,
  LexicalEditor,
  DOMExportOutput,
  EditorConfig,
  DOMConversionMap,
} from 'lexical';

export type SerializedMentionNode = Spread<
  { mention: string; user_id: string; type: 'mention'; version: 1 },
  SerializedTextNode
>;

export class MentionNode extends TextNode {
  __mention: string;
  __user_id: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.__mention,
      node.__user_id,
      node.__text,
      node.__key,
    );
  }

  constructor(mention: string, user_id: string, text?: string, key?: NodeKey) {
    super(text || mention, key);
    this.__mention = mention;
    this.__user_id = user_id;
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const mentionNode = $createMentionNode(
      serializedNode.mention,
      serializedNode.user_id,
    );
    mentionNode.setDetail(serializedNode.detail);
    mentionNode.setTextContent(serializedNode.text);
    mentionNode.setFormat(serializedNode.format);
    mentionNode.setStyle(serializedNode.style);
    mentionNode.setMode(serializedNode.mode);

    return mentionNode;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mention: this.__mention,
      user_id: this.__user_id,
      type: 'mention',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = 'iva-editor-mention';

    return dom;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', 'true');
    element.setAttribute(
      'data-lexical-mention-user_id',
      `<@${this.__user_id}>`,
    );
    element.className = 'iva-editor-mention';
    element.textContent = this.__text;

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (node) => {
        if (!node.getAttribute('data-lexical-mention')) {
          return null;
        }

        return {
          priority: 1,
          conversion: (node) => {
            const textContent = node.textContent;
            const serialUserID = node.getAttribute(
              'data-lexical-mention-user_id',
            )!;

            const user_id = serialUserID.substring(2, serialUserID.length - 1);

            if (textContent) {
              const mentionNode = $createMentionNode(textContent, user_id);

              return {
                node: mentionNode,
              };
            }

            return null;
          },
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }
}

export function $createMentionNode(
  mentionName: string,
  user_id: string,
): MentionNode {
  const mentionNode = new MentionNode(mentionName, user_id);
  mentionNode.setMode('segmented').toggleDirectionless();

  return mentionNode;
}

export function $isMentionNode(node: any): node is MentionNode {
  return node instanceof MentionNode;
}
